import { PayloadAction } from '@reduxjs/toolkit';
import { PillVariant } from 'fe-design-base/molecules/Pill/Pill';
import { RootState } from 'initializers/types';

import { getTimeDifferenceInHours } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import { CompanyLocationData } from './interfaces/ICompany';
import {
  JobRequest,
  JobRequestWeekAvailability,
} from './interfaces/IJobRequest';
import { NylasOpenHour } from './types/nylas';
import {
  HiringSliceReducers,
  HiringSliceSelectors,
  WithLoadingAndError,
} from './types/slice';
import {
  AGG_JOB_REQUEST_STATUS,
  INTERVIEW_STATUS,
  JOB_REQUEST_STATUS,
} from './constants';

export const createReducers = <S extends Record<string, unknown>>(
  initialState: S
): HiringSliceReducers<S> => {
  const reducers = {} as HiringSliceReducers<S>;

  (Object.keys(initialState) as (keyof S)[]).forEach(key => {
    const capitalized = `${String(key).charAt(0).toUpperCase()}${String(
      key
    ).slice(1)}` as Capitalize<string & keyof S>;
    const actionType = `set${capitalized}` as keyof HiringSliceReducers<S>;

    reducers[actionType] = ((
      state: S,
      action: PayloadAction<S[typeof key]>
    ) => {
      state[key] = action.payload;
    }) as HiringSliceReducers<S>[typeof actionType];
  });

  return reducers;
};

export const createSelectors = <
  S extends WithLoadingAndError<Record<string, unknown>>
>(
  sliceState: (state: RootState) => S,
  initialState: S
): HiringSliceSelectors<S> => {
  const selectors = {} as HiringSliceSelectors<S>;

  (Object.keys(initialState) as (keyof S)[]).forEach(key => {
    const stringKey = key as string;
    const capitalized = `${stringKey.charAt(0).toUpperCase()}${stringKey.slice(
      1
    )}` as Capitalize<string & keyof S>;
    const actionType = `select${capitalized}` as keyof HiringSliceSelectors<S>;

    selectors[actionType] = ((state: RootState) =>
      sliceState(state)[key]) as HiringSliceSelectors<S>[typeof actionType];
  });

  return selectors;
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const mapJobRequestStatusToAggStatus = (
  status: string | undefined
): AGG_JOB_REQUEST_STATUS => {
  switch (status) {
    case JOB_REQUEST_STATUS.ACTIVE:
      return AGG_JOB_REQUEST_STATUS.LIVE;
    case JOB_REQUEST_STATUS.DRAFT:
      return AGG_JOB_REQUEST_STATUS.DRAFT;
    case JOB_REQUEST_STATUS.PENDING_REVIEW:
      return AGG_JOB_REQUEST_STATUS.IN_REVIEW;
    case JOB_REQUEST_STATUS.FLAGGED:
      return AGG_JOB_REQUEST_STATUS.IN_REVIEW;
    case JOB_REQUEST_STATUS.ARCHIVED:
      return AGG_JOB_REQUEST_STATUS.CLOSED;
    case JOB_REQUEST_STATUS.CLOSED:
      return AGG_JOB_REQUEST_STATUS.CLOSED;
    case JOB_REQUEST_STATUS.EXPIRED:
      return AGG_JOB_REQUEST_STATUS.CLOSED;
    default:
      return AGG_JOB_REQUEST_STATUS.IN_REVIEW;
  }
};

export const isJobRequestLive = (status: string | undefined) =>
  mapJobRequestStatusToAggStatus(status) === AGG_JOB_REQUEST_STATUS.LIVE;

export const isJobRequestDraft = (status: string | undefined) =>
  mapJobRequestStatusToAggStatus(status) === AGG_JOB_REQUEST_STATUS.DRAFT;

export const convertJobRequestStatusToDisplayText = (
  status: string | undefined
): string => {
  const jobRequestStatus = mapJobRequestStatusToAggStatus(status);
  switch (jobRequestStatus) {
    case AGG_JOB_REQUEST_STATUS.LIVE:
      return toI18n('hiring.job_posts_review.status.live');
    case AGG_JOB_REQUEST_STATUS.DRAFT:
      return toI18n('hiring.job_posts_review.status.draft');
    case AGG_JOB_REQUEST_STATUS.IN_REVIEW:
      return toI18n('hiring.job_posts_review.status.in_review');
    case AGG_JOB_REQUEST_STATUS.CLOSED:
      return toI18n('hiring.job_posts_review.status.closed');
    default:
      return toI18n('hiring.job_posts_review.status.in_review');
  }
};

export const getPillVariantForJobRequestStatus = (
  status: string | undefined
): PillVariant => {
  const jobRequestStatus = mapJobRequestStatusToAggStatus(status);
  switch (jobRequestStatus) {
    case AGG_JOB_REQUEST_STATUS.LIVE:
      return 'success';
    case AGG_JOB_REQUEST_STATUS.DRAFT:
      return 'primary';
    case AGG_JOB_REQUEST_STATUS.IN_REVIEW:
      return 'info';
    case AGG_JOB_REQUEST_STATUS.CLOSED:
      return 'mono';
    default:
      return 'primary';
  }
};

export const formatJobRequirementDays = (
  week_availabilities: JobRequestWeekAvailability | undefined
) => {
  if (!week_availabilities) return '';
  const days = Object.keys(week_availabilities).filter(
    day =>
      week_availabilities[day as keyof JobRequestWeekAvailability].length > 0
  );
  if (days.includes('saturday') || days.includes('sunday'))
    return toI18n(
      'hiring.v2.job_request.job_description_page.job_details.weekends'
    );
  if (days.length === 5)
    return toI18n(
      'hiring.v2.job_request.job_description_page.job_details.weekdays'
    );
  return `${days
    .map(day =>
      capitalize(
        toI18n(`hiring.v2.job_request.job_description_page.job_details.${day}`)
      )
    )
    .join(', ')}`;
};

export const formatJobRequirementShifts = (
  week_availabilities: JobRequestWeekAvailability | undefined
) => {
  if (!week_availabilities) return '';
  const dayWithShifts = Object.keys(week_availabilities).find(
    day =>
      week_availabilities[day as keyof JobRequestWeekAvailability].length > 0
  );
  if (!dayWithShifts) return '';
  const shifts =
    week_availabilities[dayWithShifts as keyof JobRequestWeekAvailability];
  let formattedShifts = '';
  shifts.forEach(shift => {
    formattedShifts +=
      toI18n(
        `hiring.v2.job_request.job_description_page.job_details.shift_${shift}`
      ) + ', ';
  });

  return formattedShifts.slice(0, -2);
};

export const formatJobRequirementYearsOfExperience = (years: string) => {
  if (years === '0')
    return toI18n(
      'hiring.v2.job_request.job_description_page.job_details.no_experience_required'
    );
  return toI18n(
    'hiring.v2.job_request.job_description_page.job_details.years_of_experience_required',
    { props: { years } }
  );
};

export const getJobRequirementsTitle = (jobRequest: JobRequest) => {
  if (!jobRequest.employment_type) return '';
  return (
    jobRequest.employment_type +
    (jobRequest.week_availabilities?.monday?.length
      ? ', ' +
        formatJobRequirementDays(jobRequest.week_availabilities) +
        ', ' +
        formatJobRequirementShifts(jobRequest.week_availabilities)
      : '') +
    (jobRequest.years_experience
      ? ', ' +
        formatJobRequirementYearsOfExperience(jobRequest.years_experience)
      : '')
  );
};

const formatJobCompensationRangeFrequency = (frequency: string) => {
  if (frequency === 'hourly')
    return toI18n('hiring.v2.job_request.job_description_page.job_pay.hourly');
  if (frequency === 'salary')
    return toI18n('hiring.v2.job_request.job_description_page.job_pay.salary');
  return frequency;
};

const formatJobCompensationRange = (
  min: number,
  max: number | undefined | null,
  frequency: string | undefined
) => {
  const formattedFrequency = formatJobCompensationRangeFrequency(
    frequency || 'hourly'
  );
  if (!max || min === max) return `$${min}/${formattedFrequency}`;
  return `$${min} - $${max}/${formattedFrequency}`;
};

const formatJobCompensationTipsFrequency = (frequency: string) =>
  toI18n(`hiring.v2.job_request.job_description_page.job_pay.${frequency}`);

export const formatJobCompensationTips = (
  tip_amount: number | undefined,
  tip_frequency: string | undefined
) => {
  if (!tip_amount || tip_amount === 0) return '';
  const formattedFrequency = formatJobCompensationTipsFrequency(
    tip_frequency || ''
  );
  return toI18n(
    'hiring.v2.job_request.job_description_page.job_pay.tips_amount',
    { props: { tips_amt: tip_amount, tips_freq: formattedFrequency } }
  );
};

export const formatJobCompensationBenefits = (benefits: string | undefined) => {
  if (!benefits) return '';
  return toI18n(
    'hiring.v2.job_request.job_description_page.job_pay.benefits_included'
  );
};

export const getJobCompensationTitle = (
  compensation: JobRequest['job_compensation']
) => {
  if (!compensation?.target_wage_rate) return '';
  return (
    formatJobCompensationRange(
      compensation.target_wage_rate,
      compensation.target_wage_rate_max,
      compensation.wage_type
    ) +
    (compensation.tip_amount
      ? ', $' +
        formatJobCompensationTips(
          compensation.tip_amount,
          compensation.tip_frequency
        )
      : '') +
    (compensation.comments
      ? ', ' + formatJobCompensationBenefits(compensation.comments)
      : '')
  );
};

export const getJobDescriptionPromptText = (
  jobRole: string,
  type = 'part-time'
) =>
  toI18n(`hiring.v2.job_request.prompt_page.helper_text`, {
    props: {
      job_role: jobRole || 'Dishwasher',
      type,
    },
  });

export const getJobDescriptionChipText = (jobRole = 'Dishwasher') =>
  toI18n(`hiring.v2.job_request.prompt_page.helper_text_chip`, {
    props: { job_role: jobRole },
  });

export const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

export const getApplicantUniqueQualities = (
  qualities: { quote: string; rating: string; tag: string }[]
) => {
  if (!qualities) return [];
  const uniqueQualities: { tag: string; rating: string }[] = [];

  if (!!qualities) {
    const uniqueTags: Set<string> = new Set();
    for (const quality of qualities) {
      if (!uniqueTags.has(quality.tag)) {
        uniqueTags.add(quality.tag);
        uniqueQualities.push({ tag: quality.tag, rating: quality.rating });
      }
    }
  }

  return uniqueQualities;
};

export const ratingToVariant = (rating: string) => {
  switch (rating) {
    case 'positive':
      return 'success';
    case 'neutral':
      return 'mono';
    case 'negative':
      return 'mono';
    default:
      return 'mono';
  }
};

export const getCompanyAddress = (
  location: CompanyLocationData | undefined
) => {
  if (!location) return '';
  let address = '';
  if (location.address_1) address += `${location.address_1}, `;
  if (location.city) address += `${location.city}, `;
  if (location.state) address += `${location.state} `;
  if (location.zip) address += `${location.zip}`;
  return address;
};

export const convertInterviewStatusToDisplayText = (
  status: string | undefined
): string => {
  switch (status) {
    case INTERVIEW_STATUS.CONFIRMED:
      return toI18n('hiring.v2.interview_status.confirmed');
    case INTERVIEW_STATUS.NEW_TIME_PROPOSED:
      return toI18n('hiring.v2.interview_status.new_time_proposed');
    case INTERVIEW_STATUS.NOT_CONFIRMED:
      return toI18n('hiring.v2.interview_status.not_confirmed');
    default:
      return status || '';
  }
};

export const getPillVariantForInterviewStatus = (
  status: string | undefined
): PillVariant => {
  switch (status) {
    case INTERVIEW_STATUS.CONFIRMED:
      return 'success';
    case INTERVIEW_STATUS.NOT_CONFIRMED:
      return 'warning';
    case INTERVIEW_STATUS.NEW_TIME_PROPOSED:
      return 'error';
    default:
      return 'info';
  }
};

export const getTotalOpenHours = (openHours: NylasOpenHour[]) => {
  let totalHours = 0;

  openHours.forEach(({ start, end, days }) => {
    const duration = getTimeDifferenceInHours(start, end, 'HH:mm');

    // Handle overnight spans
    const normalizedDuration = duration < 0 ? duration + 24 : duration;

    totalHours += normalizedDuration * days.length;
  });

  return totalHours;
};
